import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import ConfirmModal from "../components/ConfirmModal";
import { getApiErrorMessage } from "../lib/apiErrors";
import PageGuideCard from "../components/PageGuideCard";
import ShoppingListTab from "../components/ShoppingListTab";
import { useAuth } from "../context/AuthContext";
import { appendDismissedGuide, PAGE_GUIDE_KEYS } from "../lib/pageGuides";
import { toast } from "../lib/toast";
import { FaChevronDown } from "react-icons/fa";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_PREVIEW_COUNT = 3;
const EXPANDED_CATEGORY_MAX_HEIGHT = "max-h-60";
const CREATE_CATEGORY_OPTION = "__create_category__";

function toAbsoluteUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function getRecipeImageUrl(recipe) {
  const firstImage = (recipe.images || []).find((media) => media?.image);
  return firstImage ? toAbsoluteUrl(firstImage.image) : "";
}

function formatItem(item) {
  const parts = [];
  if (item.quantity) parts.push(Number(item.quantity));
  if (item.unit) parts.push(item.unit);
  parts.push(item.name);
  return parts.join(" ");
}

function ItemPill({ item, isEditing, editValues, onEditStart, onEditChange, onEditSave, onEditCancel, onRemove, onDragStart }) {
  const pillRef = useRef(null);
  const quantityRef = useRef(null);

  useEffect(() => {
    if (isEditing && quantityRef.current) {
      quantityRef.current.focus();
      quantityRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onEditSave(item.id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEditCancel();
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!pillRef.current?.contains(document.activeElement)) {
        onEditSave(item.id);
      }
    }, 0);
  };

  if (isEditing) {
    return (
      <div
        ref={pillRef}
        className="group inline-flex items-center gap-1.5 rounded-2xl border border-blue-300 bg-white px-3 py-1.5 shadow-sm transition dark:border-blue-500 dark:bg-gray-800"
      >
        <input
          ref={quantityRef}
          type="text"
          value={editValues.quantity}
          onChange={(e) => onEditChange({ ...editValues, quantity: e.target.value })}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="qty"
          className="w-12 bg-transparent border-b border-blue-300 dark:border-blue-500 text-sm text-gray-700 dark:text-gray-200 text-center outline-none placeholder-gray-300 dark:placeholder-gray-600"
        />
        <input
          type="text"
          value={editValues.unit}
          onChange={(e) => onEditChange({ ...editValues, unit: e.target.value })}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="unit"
          className="w-10 bg-transparent border-b border-blue-300 dark:border-blue-500 text-sm text-gray-700 dark:text-gray-200 text-center outline-none placeholder-gray-300 dark:placeholder-gray-600"
        />
        <span className="text-sm text-gray-700 dark:text-gray-200">{item.name}</span>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item)}
      className="group inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/90 px-3.5 py-2 shadow-[0_8px_24px_-18px_rgba(15,23,42,0.45)] transition hover:border-slate-300 hover:shadow-[0_14px_32px_-20px_rgba(15,23,42,0.5)] cursor-grab active:cursor-grabbing dark:border-gray-700 dark:bg-gray-800"
    >
      <span
        onClick={() => onEditStart(item)}
        className="cursor-pointer text-sm text-slate-700 transition-colors hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400"
      >
        {formatItem(item)}
      </span>
      <button
        onClick={() => onRemove(item.id)}
        className="opacity-0 group-hover:opacity-100 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400 text-[10px] transition-all"
      >
        &times;
      </button>
    </div>
  );
}

function CategoryZone({
  name, items: zoneItems, isUncategorized, subtitle,
  dragOverCategory, onDragOver, onDragLeave, onDrop, onDeleteCategory,
  editingItemId, editValues, onEditStart, onEditChange, onEditSave, onEditCancel,
  onRemove, onDragStart, isExpanded, onToggleExpand,
}) {
  const isOver = dragOverCategory === (isUncategorized ? "" : name);
  const dropTarget = isUncategorized ? "" : name;
  const isEmpty = zoneItems.length === 0;
  const canExpand = zoneItems.length > CATEGORY_PREVIEW_COUNT;
  const visibleItems = isExpanded ? zoneItems : zoneItems.slice(0, CATEGORY_PREVIEW_COUNT);
  const hiddenCount = Math.max(zoneItems.length - CATEGORY_PREVIEW_COUNT, 0);

  return (
    <div
      onDragOver={(e) => onDragOver(e, dropTarget)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, dropTarget)}
      className={`rounded-[1.4rem] p-5 transition-all ${
        isOver
          ? "border border-blue-300 bg-blue-50/80 shadow-[0_20px_45px_-30px_rgba(37,99,235,0.45)] dark:bg-blue-900/10"
          : "border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.96))] shadow-[0_20px_45px_-38px_rgba(15,23,42,0.32)] dark:border-gray-700 dark:bg-gray-800/60 dark:bg-none"
      }`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[0.95rem] font-semibold tracking-[0.05em] text-slate-800 dark:text-gray-100">
              {isUncategorized ? "Uncategorized" : name}
            </h3>
            <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-medium text-slate-500 shadow-sm dark:bg-gray-800 dark:text-gray-400">
              {zoneItems.length}
            </span>
          </div>
          {(subtitle || isUncategorized) && (
            <p className="type-muted mt-1 text-xs leading-5">
              {subtitle || "Items waiting to be sorted into a shelf."}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canExpand && (
            <button
              onClick={onToggleExpand}
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 text-[11px] text-slate-500 transition hover:border-slate-300 hover:text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {!isExpanded && hiddenCount > 0 && <span>+{hiddenCount} more</span>}
              <FaChevronDown
                className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              />
            </button>
          )}
          {!isUncategorized && isEmpty && (
            <button
              onClick={() => onDeleteCategory(name)}
              className="text-[11px] text-gray-400 transition hover:text-red-500"
            >
              remove
            </button>
          )}
        </div>
      </div>
      {isEmpty ? (
        <p className="type-muted text-xs italic">
          {isUncategorized ? "Items without a category appear here" : "Drag items here"}
        </p>
      ) : (
        <div className={isExpanded ? `${EXPANDED_CATEGORY_MAX_HEIGHT} overflow-y-auto pr-1` : ""}>
          <div className="flex flex-wrap gap-2.5">
            {visibleItems.map((item) => (
              <ItemPill
                key={item.id}
                item={item}
                isEditing={editingItemId === item.id}
                editValues={editValues}
                onEditStart={onEditStart}
                onEditChange={onEditChange}
                onEditSave={onEditSave}
                onEditCancel={onEditCancel}
                onRemove={onRemove}
                onDragStart={onDragStart}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PantryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tab = ["pantry", "shopping", "suggested"].includes(requestedTab) ? requestedTab : "pantry";
  const { currentUser, updateCurrentUser } = useAuth();

  // Pantry state
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState("");
  const [inputCategory, setInputCategory] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [addError, setAddError] = useState(null);

  // Drag state
  const [dragOverCategory, setDragOverCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Edit state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValues, setEditValues] = useState({ quantity: '', unit: '' });
  const editingRef = useRef(null);

  // Suggested state
  const [suggested, setSuggested] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [minPercent, setMinPercent] = useState(0);

  const fetchPantry = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/pantry/");
      setItems(res.data.items || []);
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error("Failed to fetch pantry:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuggested = useCallback(async () => {
    setSuggestedLoading(true);
    try {
      const res = await axiosInstance.get("/api/pantry/suggested/", {
        params: { min_percent: minPercent },
      });
      setSuggested(res.data);
    } catch (err) {
      console.error("Failed to fetch suggested recipes:", err);
    } finally {
      setSuggestedLoading(false);
    }
  }, [minPercent]);

  useEffect(() => { fetchPantry(); }, [fetchPantry]);
  useEffect(() => { if (tab === "suggested") fetchSuggested(); }, [tab, fetchSuggested]);

  // Group items by category (single pass)
  const uncategorized = [];
  const itemsByCategory = {};
  for (const item of items) {
    if (!item.category) {
      uncategorized.push(item);
    } else {
      (itemsByCategory[item.category] ||= []).push(item);
    }
  }
  const sortedCategories = [...categories].sort((a, b) => {
    const countA = (itemsByCategory[a] || []).length;
    const countB = (itemsByCategory[b] || []).length;
    if (countA !== countB) return countB - countA;
    return a.localeCompare(b);
  });
  const activeCategories = sortedCategories.filter((cat) => (itemsByCategory[cat] || []).length > 0);
  const resolvedInputCategory = inputCategory === CREATE_CATEGORY_OPTION
    ? newCategoryName.trim()
    : inputCategory;
  const canSubmitAdd = inputText.trim() && (
    inputCategory !== CREATE_CATEGORY_OPTION || newCategoryName.trim()
  );

  // Add item
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!canSubmitAdd) return;
    try {
      await axiosInstance.post("/api/pantry/", {
        text: inputText.trim(),
        category: resolvedInputCategory,
      }, { _skipGlobalErrorToast: true });
      setInputText("");
      setInputCategory("");
      setNewCategoryName("");
      toast.success("Item added to pantry.");
      fetchPantry();
      if (tab === "suggested") fetchSuggested();
    } catch (err) {
      console.error("Failed to add item:", err);
      setAddError({
        title: "Unable to add pantry item",
        message: getApiErrorMessage(err, "Unable to add this item. Please try again."),
      });
    }
  };

  // Bulk add
  const handleBulkAdd = async () => {
    if (!bulkText.trim()) return;
    const itemsList = bulkText.split(",").map((s) => s.trim()).filter(Boolean);
    if (!itemsList.length) return;
    try {
      await axiosInstance.post("/api/pantry/bulk/", { items: itemsList }, {
        _skipGlobalErrorToast: true,
      });
      setBulkText("");
      setBulkOpen(false);
      toast.success("Items added to pantry.");
      fetchPantry();
      if (tab === "suggested") fetchSuggested();
    } catch (err) {
      console.error("Failed to bulk add:", err);
      setAddError({
        title: "Unable to add pantry items",
        message: getApiErrorMessage(err, "Unable to add these items. Please try again."),
      });
    }
  };

  // Remove item
  const handleRemove = async (id) => {
    try {
      await axiosInstance.delete(`/api/pantry/${id}/`);
      setItems((prev) => {
        const itemToRemove = prev.find((item) => item.id === id);
        const nextItems = prev.filter((item) => item.id !== id);
        if (
          itemToRemove?.category &&
          !nextItems.some((item) => item.category === itemToRemove.category)
        ) {
          setCategories((prevCategories) =>
            prevCategories.filter((category) => category !== itemToRemove.category)
          );
        }
        return nextItems;
      });
      toast.success("Item removed from pantry.");
      if (tab === "suggested") fetchSuggested();
    } catch (err) {
      console.error("Failed to remove item:", err);
      toast.error("Failed to remove pantry item.");
    }
  };

  // Clear all
  const handleClear = async () => {
    try {
      await axiosInstance.delete("/api/pantry/clear/");
      setItems([]);
      setCategories([]);
      setShowClearConfirm(false);
      setSuggested([]);
      toast.success("Pantry cleared.");
    } catch (err) {
      console.error("Failed to clear pantry:", err);
      toast.error("Failed to clear pantry.");
    }
  };

  // Drag and drop
  const handleDragStart = (e, item) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ itemId: item.id }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, cat) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCategory(cat);
  };

  const handleDragLeave = () => setDragOverCategory(null);

  const handleDrop = async (e, category) => {
    e.preventDefault();
    setDragOverCategory(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.itemId) {
        const movedItem = items.find((item) => item.id === data.itemId);
        const previousCategory = movedItem?.category || "";
        await axiosInstance.patch(`/api/pantry/${data.itemId}/`, { category });
        setItems((prev) => {
          const nextItems = prev.map((item) =>
            item.id === data.itemId ? { ...item, category } : item
          );
          if (
            previousCategory &&
            previousCategory !== category &&
            !nextItems.some((item) => item.category === previousCategory)
          ) {
            setCategories((prevCategories) =>
              prevCategories.filter((name) => name !== previousCategory)
            );
          }
          return nextItems;
        });
        if (category) {
          setCategories((prev) =>
            prev.includes(category) ? prev : [...prev, category].sort((a, b) => a.localeCompare(b))
          );
        }
        toast.success("Item moved.");
      }
    } catch (err) {
      console.error("Failed to move item:", err);
      toast.error("Failed to move pantry item.");
    }
  };

  // Inline edit handlers
  const handleEditStart = (item) => {
    setEditingItemId(item.id);
    editingRef.current = item.id;
    setEditValues({
      quantity: item.quantity ? String(Number(item.quantity)) : '',
      unit: item.unit || '',
    });
  };

  const handleEditSave = async (itemId) => {
    if (editingRef.current !== itemId) return;
    editingRef.current = null;
    setEditingItemId(null);

    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    const newQuantity = editValues.quantity.trim() === '' ? null : Number(editValues.quantity);
    const newUnit = editValues.unit.trim();

    if (newQuantity !== null && isNaN(newQuantity)) return;

    const oldQuantity = item.quantity;
    const oldUnit = item.unit;

    // Skip API call if nothing changed
    const sameQty = (newQuantity === null && !item.quantity) ||
      (newQuantity !== null && item.quantity && Number(item.quantity) === newQuantity);
    const sameUnit = newUnit === (item.unit || '');
    if (sameQty && sameUnit) return;

    // Optimistic update
    setItems((prev) =>
      prev.map((i) => i.id === itemId ? { ...i, quantity: newQuantity !== null ? String(newQuantity) : null, unit: newUnit } : i)
    );

    try {
      await axiosInstance.patch(`/api/pantry/${itemId}/`, { quantity: newQuantity, unit: newUnit });
      toast.success("Item updated.");
    } catch (err) {
      console.error("Failed to update item:", err);
      toast.error("Failed to update pantry item.");
      setItems((prev) =>
        prev.map((i) => i.id === itemId ? { ...i, quantity: oldQuantity, unit: oldUnit } : i)
      );
    }
  };

  const handleEditCancel = () => {
    editingRef.current = null;
    setEditingItemId(null);
  };

  const getMatchColor = (percent) => {
    if (percent >= 80) return "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20";
    if (percent >= 50) return "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20";
    return "text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700";
  };

  const getCategoryExpandKey = useCallback(
    (name, isUncategorized = false) => (isUncategorized ? "__uncategorized__" : name),
    []
  );

  const handleToggleCategoryExpand = useCallback((name, isUncategorized = false) => {
    const key = getCategoryExpandKey(name, isUncategorized);
    setExpandedCategories((prev) => (prev[key] ? {} : { [key]: true }));
  }, [getCategoryExpandKey]);

  const setActiveTab = useCallback((nextTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      return next;
    });
  }, [setSearchParams]);

  // Shared props for CategoryZone
  const zoneProps = {
    dragOverCategory,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    editingItemId,
    editValues,
    onEditStart: handleEditStart,
    onEditChange: setEditValues,
    onEditSave: handleEditSave,
    onEditCancel: handleEditCancel,
    onRemove: handleRemove,
    onDragStart: handleDragStart,
  };

  const pageTitle = tab === "shopping"
    ? "Shopping List"
    : tab === "suggested"
    ? "Recipe Ideas"
    : "My Pantry";

  const handleDismissOnboarding = useCallback(async (guideKey) => {
    try {
      await updateCurrentUser({
        onboarding_dismissed_pages: appendDismissedGuide(currentUser, guideKey),
      });
    } catch (err) {
      console.error("Failed to dismiss onboarding:", err);
    }
  }, [currentUser, updateCurrentUser]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="type-page-title mb-6">{pageTitle}</h1>

      {tab === "pantry" && (
        <PageGuideCard
          onboarding={currentUser}
          guideKey={PAGE_GUIDE_KEYS.PANTRY}
          title="This is your pantry"
          description="Add the ingredients you already have at home so CartNom can suggest recipes, avoid duplicate shopping, and keep your kitchen inventory organized."
          ctaLabel="Add your first ingredient"
          ctaHref="/pantry?tab=pantry"
          onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.PANTRY)}
        />
      )}

      {tab === "shopping" && (
        <PageGuideCard
          onboarding={currentUser}
          guideKey={PAGE_GUIDE_KEYS.SHOPPING}
          title="This is your shopping list"
          description="Add the ingredients you still need to buy, then check them off after shopping to move them into your pantry."
          ctaLabel="Start shopping"
          ctaHref="/pantry?tab=shopping"
          onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.SHOPPING)}
        />
      )}

      {/* Pantry Tab */}
      {tab === "pantry" && (
        <>
          <div className="mb-6 flex flex-col gap-5">
            <div>
              <p className="type-muted">
                {items.length} ingredient{items.length !== 1 ? "s" : ""} in your pantry.
              </p>
            </div>

            <div className="rounded-[1.6rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.95))] p-5 shadow-[0_28px_60px_-48px_rgba(15,23,42,0.4)] dark:border-gray-700 dark:bg-gray-800/70 dark:bg-none">
              <form onSubmit={handleAdd} className="flex flex-col gap-3 lg:flex-row">
                <input
                  type="text"
                  placeholder='Type an ingredient (e.g. "3 lbs flour")'
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  autoFocus
                  className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-900/30"
                />
                <select
                  value={inputCategory}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setInputCategory(nextValue);
                    if (nextValue !== CREATE_CATEGORY_OPTION) {
                      setNewCategoryName("");
                    }
                  }}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300 dark:focus:border-blue-500 dark:focus:ring-blue-900/30"
                >
                  <option value="">No category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value={CREATE_CATEGORY_OPTION}>+ Create new category</option>
                </select>
                <button
                  type="submit"
                  disabled={!canSubmitAdd}
                  className="rounded-2xl bg-blue-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-600 disabled:opacity-40"
                >
                  Add
                </button>
              </form>
              {inputCategory === CREATE_CATEGORY_OPTION && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-900/30"
                  />
                </div>
              )}
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setBulkOpen(!bulkOpen)}
                  className="text-sm text-blue-500 transition hover:text-blue-600"
                >
                  {bulkOpen ? "Hide bulk add" : "Add multiple at once"}
                </button>
                {items.length > 0 && (
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    className="ml-auto text-sm text-slate-400 transition hover:text-red-500"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {bulkOpen && (
                <div className="mt-4 border-t border-slate-200 pt-4 dark:border-gray-700">
                  <textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder="3 lbs flour, 5 tomatoes, chicken breast, salt, pepper"
                    rows={2}
                    className="mb-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-900/30"
                  />
                  <button
                    onClick={handleBulkAdd}
                    disabled={!bulkText.trim()}
                    className="rounded-2xl bg-blue-500 px-4 py-2 text-xs font-medium text-white transition hover:bg-blue-600 disabled:opacity-40"
                  >
                    Add All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Item display */}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : items.length === 0 && categories.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <span className="text-2xl">🥬</span>
              </div>
              <p className="type-card-title mb-1">Your pantry is empty</p>
              <p className="type-muted">Add what you have at home to get recipe suggestions</p>
            </div>
          ) : (
            <>
              <section className="mb-8">
                <div className="mb-4 flex items-end justify-end gap-3">
                  <p className="type-muted text-xs">
                    Drag ingredients between shelves to reorganize.
                  </p>
                </div>

                {(uncategorized.length > 0 || activeCategories.length > 0) ? (
                  <>
                    {uncategorized.length > 0 && (
                      <div className="mb-4">
                        <CategoryZone
                          name=""
                          items={uncategorized}
                          isUncategorized
                          subtitle="A gentle holding space for anything that still needs a home."
                          isExpanded={!!expandedCategories[getCategoryExpandKey("", true)]}
                          onToggleExpand={() => handleToggleCategoryExpand("", true)}
                          {...zoneProps}
                        />
                      </div>
                    )}

                    {activeCategories.length > 0 && (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {activeCategories.map((cat) => (
                          <CategoryZone
                            key={cat}
                            name={cat}
                            items={itemsByCategory[cat] || []}
                            isExpanded={!!expandedCategories[getCategoryExpandKey(cat)]}
                            onToggleExpand={() => handleToggleCategoryExpand(cat)}
                            {...zoneProps}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-[1.4rem] border border-dashed border-slate-200 bg-slate-50/70 px-5 py-10 text-center dark:border-gray-700 dark:bg-gray-800/50">
                    <p className="type-card-title mb-1">No stocked categories yet</p>
                    <p className="type-muted">Add an ingredient or drag one into a category to start filling your pantry shelves.</p>
                  </div>
                )}
              </section>

            </>
          )}
        </>
      )}

      {/* Shopping List Tab */}
      {tab === "shopping" && (
        <ShoppingListTab
          onPantryUpdate={() => {
            fetchPantry();
          }}
        />
      )}

      {/* Suggested Recipes Tab */}
      {tab === "suggested" && (
        <>
          <PageGuideCard
            onboarding={currentUser}
            guideKey={PAGE_GUIDE_KEYS.RECIPE_IDEAS}
            title="These are recipes you can likely make next"
            description="CartNom compares your pantry against saved recipes and highlights the strongest matches first, so you can spot what is already cookable and what only needs a few missing ingredients."
            ctaLabel="Add more pantry items"
            ctaHref="/pantry?tab=pantry"
            onDismiss={() => handleDismissOnboarding(PAGE_GUIDE_KEYS.RECIPE_IDEAS)}
          />

          {/* Match filter */}
          {items.length > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">Min match:</span>
              <select
                value={minPercent}
                onChange={(e) => setMinPercent(Number(e.target.value))}
                className="bg-gray-50 dark:bg-gray-700 border-0 px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-600 outline-none"
              >
                <option value={0}>All matches</option>
                <option value={25}>25%+</option>
                <option value={50}>50%+</option>
                <option value={75}>75%+</option>
                <option value={90}>90%+</option>
              </select>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {suggested.length} recipe{suggested.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {items.length === 0 ? (
            <div className="text-center py-12">
              <p className="type-muted mb-2">Add ingredients to your pantry first</p>
              <button
                onClick={() => setActiveTab("pantry")}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                Go to My Pantry
              </button>
            </div>
          ) : suggestedLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : suggested.length === 0 ? (
            <p className="type-muted text-center py-12">
              No matching recipes found. Try adding more ingredients to your pantry.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {suggested.map((recipe) => {
                const imageUrl = getRecipeImageUrl(recipe);

                return (
                  <Link
                    key={recipe.id}
                    to={`/recipes/${recipe.id}`}
                    className="block bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/50 overflow-hidden hover:shadow-lg transition"
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={recipe.title}
                        className="w-full h-36 object-cover"
                      />
                    ) : (
                      <div className="w-full h-36 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-xs">
                        No image
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white truncate mb-1">
                        {recipe.title}
                      </h3>
                      <div className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full mb-2 ${getMatchColor(recipe.match_percent)}`}>
                        {recipe.match_count}/{recipe.total_ingredients} ({recipe.match_percent}%)
                      </div>
                      {recipe.missing_ingredients?.length > 0 && (
                        <div className="mt-1">
                          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-0.5">
                            Missing ({recipe.missing_ingredients.length}):
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {recipe.missing_ingredients.map((ing, i) => (
                              <span
                                key={i}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                              >
                                {ing}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-2 text-[11px] text-gray-400">
                        {recipe.category && <span>{recipe.category}</span>}
                        {recipe.prep_time && <span> · {recipe.prep_time}m prep</span>}
                        {recipe.cook_time && <span> · {recipe.cook_time}m cook</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}

      {addError && (
        <ConfirmModal
          title={addError.title}
          message={addError.message}
          variant="info"
          confirmText="OK"
          cancelText={null}
          onConfirm={() => setAddError(null)}
          onCancel={() => setAddError(null)}
        />
      )}

      {/* Clear confirm */}
      {showClearConfirm && (
        <ConfirmModal
          title="Clear Pantry"
          message="Remove all ingredients from your pantry?"
          variant="danger"
          confirmText="Clear All"
          cancelText="Cancel"
          onConfirm={handleClear}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}
    </div>
  );
}
